import { Dispatch, MouseEventHandler, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Modal } from '@/lib/components/Modal';
import { Button, Input, Text } from '@/lib/v2/components';
import { Divide, Flex } from '@/lib/v2/components/Layout/Stack';

import { SEGMENT_PATH } from '@/modules/ContactsModule/constants';
import { useMutationCreateSegmentContext } from '@/modules/ContactsModule/contexts/CreateSegmentContext';

const ButtonsSection = ({
  onAction,
  onClose,
}: {
  onAction?: MouseEventHandler<HTMLButtonElement>;
  onClose?: MouseEventHandler<HTMLButtonElement>;
}) => {
  const { t } = useTranslation();
  return (
    <div className="pt-4">
      <Flex withGap alignment="end" gapSize="small">
        <Button outline onClick={onClose}>
          <div className="text-12 lg:text-12 xl:text-14 2xl:text-16">
            {t('CREATE_SEGMENT_MODAL.close')}
          </div>
        </Button>
        <Button typeButton="submit" onClick={onAction}>
          <div className="text-12 lg:text-12 xl:text-14 2xl:text-16">
            {t('CREATE_SEGMENT_MODAL.confirm')}
          </div>
        </Button>
      </Flex>
    </div>
  );
};

type FormInput = {
  segment: string;
};

export const CreateSegmentModal = ({
  setShowModal,
}: {
  setShowModal?: Dispatch<SetStateAction<boolean>>;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInput>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const RULES = {
    segment: {
      required: t('CREATE_SEGMENT_MODAL.emptyField'),
    },
  };

  const onAction = handleSubmit((values) => {
    const { segment: segmentName } = values;
    navigate(`${SEGMENT_PATH}/create-segment/${segmentName}`);
    setShowModal && setShowModal(false);
  });

  return (
    <Modal isFloat={true}>
      <div className="h-full p-6">
        <Divide>
          <Flex column gapSize="small" itemAlignment="stretch">
            <Modal.Title buttonClose={false} onAction={() => setShowModal && setShowModal(false)}>
              <Text color="light-black" fontWeight="bold">
                {t('CREATE_SEGMENT_MODAL.title')}
              </Text>
            </Modal.Title>
            <div className="h-[86%]">
              <Modal.Body>
                <div className="w-[30vw] p-[1%] py-4">
                  <div className="w-full">
                    <section className="mb-3">
                      <div className="pt-4">
                        <Input
                          {...register('segment', RULES.segment)}
                          error={errors?.segment && true}
                          label={t('CREATE_SEGMENT_MODAL.fieldName')}
                          message={errors?.segment?.message}
                        />
                      </div>
                    </section>
                  </div>
                </div>
              </Modal.Body>
            </div>
            <Modal.Footer>
              <ButtonsSection
                onAction={onAction}
                onClose={() => setShowModal && setShowModal(false)}
              />
            </Modal.Footer>
          </Flex>
        </Divide>
      </div>
    </Modal>
  );
};
